/**
 * ErrorBoundary.tsx
 *
 * React error boundary that reports uncaught render errors via `captureError`.
 * Wraps Sentry's `withErrorBoundary` HOC for convenience but also exposes a
 * bare `<HostesaErrorBoundary>` component for manual tree wrapping.
 *
 * Usage (declarative):
 *   <HostesaErrorBoundary fallback={<ErrorState />} routeId="/live-calls">
 *     <LiveCallPanel />
 *   </HostesaErrorBoundary>
 *
 * Usage (HOC):
 *   export default withSentryErrorBoundary(MyComponent, { fallback: <ErrorState /> });
 */

import { Component, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { captureError, type ErrorContext } from '../sentry.js';

export type ErrorBoundaryFallback =
  | ReactNode
  | ((props: { error: Error; reset: () => void }) => ReactNode);

export type HostesaErrorBoundaryProps = {
  children: ReactNode;
  /**
   * Fallback UI to render when an error is caught.
   * Can be a static node or a render function receiving the error and a reset callback.
   */
  fallback?: ErrorBoundaryFallback;
  /**
   * Optional safe context attached to the Sentry report.
   * Must NOT include PII — captureError scrubs it, but avoid passing it.
   */
  context?: ErrorContext;
  /** Called when an error is caught, after reporting. */
  onError?: (error: Error, info: ErrorInfo) => void;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

export class HostesaErrorBoundary extends Component<HostesaErrorBoundaryProps, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureError(error, this.props.context);
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (!this.state.hasError || this.state.error === null) {
      return this.props.children;
    }

    const { fallback } = this.props;

    if (!fallback) {
      // Minimal default fallback — intentionally plain (no brand dependency).
      return (
        <div role="alert" data-testid="error-boundary-fallback">
          <p>Something went wrong. Please refresh the page.</p>
        </div>
      );
    }

    if (typeof fallback === 'function') {
      return fallback({ error: this.state.error, reset: this.reset });
    }

    return fallback;
  }
}

/**
 * Higher-order component that wraps a component with `HostesaErrorBoundary`.
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: Omit<HostesaErrorBoundaryProps, 'children'> = {},
): ComponentType<P> {
  const displayName = WrappedComponent.displayName ?? WrappedComponent.name ?? 'Component';

  function WithErrorBoundary(props: P): ReactNode {
    return (
      <HostesaErrorBoundary {...options}>
        <WrappedComponent {...props} />
      </HostesaErrorBoundary>
    );
  }

  WithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  return WithErrorBoundary;
}
